import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Badge, Button, Input, Separator, Textarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useNoticeCanonicalLinks } from '../hooks/useCanonicalOpportunities';
import { useNoticeSourceCoverage, useRecordManualIntel } from '../hooks/useNoticeCoverage';
const WARN = 'border-warning/25 bg-warning-muted text-warning';
const BAD = 'border-danger/25 bg-danger-muted text-danger';
/** Only 'current' is a success style; every other state is explicitly not-complete. */ const STATE_STYLES = {
    current: {
        label: 'Current',
        className: 'border-success/20 bg-success-muted text-success',
        complete: true
    },
    partial: {
        label: 'Partial coverage — not complete',
        className: WARN,
        complete: false
    },
    unknown: {
        label: 'Coverage unknown',
        className: WARN,
        complete: false
    },
    stale: {
        label: 'Stale — may be out of date',
        className: WARN,
        complete: false
    },
    no_results: {
        label: 'Searched — no results found',
        className: WARN,
        complete: false
    },
    unavailable: {
        label: 'Unavailable — access not available',
        className: BAD,
        complete: false
    },
    failed: {
        label: 'Failed — retrieval error',
        className: BAD,
        complete: false
    }
};
/** Unrecognised states fall back to unknown so nothing unexpected can look complete. */ export function coverageStateStyle(state) {
    return STATE_STYLES[state] ?? STATE_STYLES.unknown;
}
const NEEDS_MANUAL = new Set([
    'unavailable',
    'failed'
]);
function formatTime(ts) {
    return ts ? ts.toDate().toLocaleString() : 'never retrieved';
}
function SourceRow({ row }) {
    const style = coverageStateStyle(row.state);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1 text-sm",
        "data-testid": Testids.noticeCoverage.sourceRow,
        "data-source": row.source,
        "data-state": row.state,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: "font-medium",
                        children: [
                            row.source,
                            row.sourceIdentifier ? ` · ${row.sourceIdentifier}` : ''
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Badge, {
                        variant: "outline",
                        className: style.className,
                        "data-testid": Testids.noticeCoverage.sourceState,
                        children: style.label
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: "text-xs text-muted-foreground",
                children: [
                    "Retrieved: ",
                    formatTime(row.retrievedAt)
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: "text-xs text-muted-foreground",
                children: [
                    "Materials obtained:",
                    ' ',
                    row.materialsObtained.length > 0 ? row.materialsObtained.join(', ') : 'none'
                ]
            }),
            row.detail && /*#__PURE__*/ _jsx("p", {
                className: "text-xs",
                children: row.detail
            })
        ]
    });
}
function ManualIntelForm({ opportunityId, noticeId }) {
    const record = useRecordManualIntel(opportunityId);
    const [intelNoticeId, setIntelNoticeId] = useState('');
    const [parent, setParent] = useState('');
    const [provenance, setProvenance] = useState('');
    const [materials, setMaterials] = useState('');
    const submit = (e)=>{
        e.preventDefault();
        const trimmed = provenance.trim();
        const id = intelNoticeId.trim();
        if (!trimmed || !id) return;
        record.mutate({
            noticeId: id,
            parentNoticeId: parent.trim(),
            provenance: trimmed,
            materialsObtained: materials.split('\n').map((m)=>m.trim()).filter(Boolean)
        }, {
            onSuccess: ()=>{
                setIntelNoticeId('');
                setParent('');
                setProvenance('');
                setMaterials('');
            },
            onError: ()=>toast.error('Could not record manual intel')
        });
    };
    return /*#__PURE__*/ _jsxs("form", {
        className: "space-y-2",
        onSubmit: submit,
        "data-testid": Testids.noticeCoverage.manualForm,
        children: [
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm font-medium",
                children: "Record manual intel"
            }),
            /*#__PURE__*/ _jsx(Input, {
                placeholder: "Notice ID",
                "aria-label": "Notice ID",
                value: intelNoticeId,
                onChange: (e)=>setIntelNoticeId(e.target.value),
                "data-testid": Testids.noticeCoverage.manualNoticeId
            }),
            /*#__PURE__*/ _jsx(Input, {
                placeholder: `Amendment of (parent notice, e.g. ${noticeId}) — optional`,
                "aria-label": "Parent notice ID",
                value: parent,
                onChange: (e)=>setParent(e.target.value),
                "data-testid": Testids.noticeCoverage.manualParentNoticeId
            }),
            /*#__PURE__*/ _jsx(Input, {
                placeholder: "Received from / how (required)",
                "aria-label": "Provenance",
                value: provenance,
                onChange: (e)=>setProvenance(e.target.value),
                "data-testid": Testids.noticeCoverage.manualProvenance
            }),
            /*#__PURE__*/ _jsx(Textarea, {
                placeholder: "Materials obtained, one per line",
                "aria-label": "Materials obtained",
                value: materials,
                onChange: (e)=>setMaterials(e.target.value),
                "data-testid": Testids.noticeCoverage.manualMaterials
            }),
            /*#__PURE__*/ _jsx(Button, {
                type: "submit",
                size: "sm",
                disabled: record.isPending || !provenance.trim() || !intelNoticeId.trim(),
                "data-testid": Testids.noticeCoverage.manualSubmit,
                children: "Record intel"
            })
        ]
    });
}
function CoveragePanel({ opportunityId, noticeId }) {
    const { data: rows = [], isLoading, error } = useNoticeSourceCoverage(opportunityId);
    const complete = rows.length > 0 && rows.every((r)=>coverageStateStyle(r.state).complete);
    const needsManual = rows.some((r)=>NEEDS_MANUAL.has(r.state));
    let summary;
    let summaryClass;
    if (rows.length === 0) {
        summary = 'Coverage unknown — no source has reported';
        summaryClass = WARN;
    } else if (complete) {
        summary = 'All sources current';
        summaryClass = STATE_STYLES.current.className;
    } else {
        summary = 'Not complete — see sources below';
        summaryClass = WARN;
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "ml-6 space-y-3",
        "data-testid": Testids.noticeCoverage.panel,
        "data-opportunity-id": opportunityId,
        children: [
            isLoading && /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "Loading…"
            }),
            error && /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "Could not load source coverage."
            }),
            !isLoading && !error && /*#__PURE__*/ _jsx("p", {
                className: `rounded border px-2 py-1 text-sm ${summaryClass}`,
                "data-testid": Testids.noticeCoverage.summary,
                "data-complete": complete,
                children: summary
            }),
            rows.map((row)=>/*#__PURE__*/ _jsx(SourceRow, {
                    row: row
                }, row.id || row.source)),
            needsManual && /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "A source could not be read. Record anything learned by other means below."
            }),
            /*#__PURE__*/ _jsx(ManualIntelForm, {
                opportunityId: opportunityId,
                noticeId: noticeId
            })
        ]
    });
}
/**
 * Notice detail section: which sources have been checked for the Notice's
 * Opportunity, when, and what was actually obtained. Coverage is keyed by
 * canonical Opportunity, so a Notice linked to none reports unknown (SAD-466).
 */ export function NoticeCoverage({ noticeId }) {
    const { linked } = useNoticeCanonicalLinks(noticeId);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2 text-sm text-muted-foreground mb-2",
                        children: [
                            /*#__PURE__*/ _jsx(ShieldAlert, {
                                className: "h-4 w-4"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-medium",
                                children: "Source coverage"
                            })
                        ]
                    }),
                    linked.length === 0 ? /*#__PURE__*/ _jsxs("div", {
                        className: "ml-6",
                        "data-testid": Testids.noticeCoverage.panel,
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: `rounded border px-2 py-1 text-sm ${WARN}`,
                                "data-testid": Testids.noticeCoverage.summary,
                                "data-complete": false,
                                children: "Coverage unknown — no source has reported"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "mt-2 text-xs text-muted-foreground",
                                children: "Link this Notice to an Opportunity to record manual intel."
                            })
                        ]
                    }) : linked.map((l)=>/*#__PURE__*/ _jsx(CoveragePanel, {
                            opportunityId: l.opportunityId,
                            noticeId: noticeId
                        }, l.opportunityId))
                ]
            }),
            /*#__PURE__*/ _jsx(Separator, {})
        ]
    });
}
